/**
 *
 * Copyright 2020-2026 Splunk Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

import { Attributes, diag } from '@opentelemetry/api'
import { hrTimeToMilliseconds } from '@opentelemetry/core'
import { Span, SpanProcessor } from '@opentelemetry/sdk-trace-base'

import { SESSION_DURATION_MS, SessionManager, UserManager } from '../managers'

export class SpanAttributesProcessor implements SpanProcessor {
	private readonly _globalAttributes: Attributes

	constructor(
		private sessionManager: SessionManager,
		private userManager: UserManager,
		globalAttributes: Attributes,
		private readonly _experimental_discardDataAfterInactivity: boolean = false,
		private readonly _experimental_adjustSessionStartToTimeOrigin: boolean = false,
	) {
		this._globalAttributes = globalAttributes ?? {}
	}

	forceFlush(): Promise<void> {
		return Promise.resolve()
	}

	getGlobalAttributes(): Attributes {
		return this._globalAttributes
	}

	onEnd(span: Span): void {
		const spanEndTimeSeconds = span.endTime[0]
		const sessionId = (span.attributes['splunk.rumSessionId'] as string | undefined) ?? ''
		const sessionState = this.sessionManager.sessionHistory.get(sessionId)
		if (sessionState) {
			const sessionMaxDurationTimeInSeconds = (sessionState.startTime + SESSION_DURATION_MS) / 1000
			// If there is a long span for example fetch that takes hours and the session expires, we need to truncate the span
			// to the session expiration time
			if (spanEndTimeSeconds > sessionMaxDurationTimeInSeconds) {
				span.endTime[0] = sessionMaxDurationTimeInSeconds
				span.endTime[1] = 0
				span.duration[0] = sessionMaxDurationTimeInSeconds - span.startTime[0]
				span.duration[1] = 0
			}
		}
	}

	onStart(span: Span): void {
		span.setAttribute('location.href', location.href)
		span.setAttributes(this._globalAttributes)

		const sessionState = this.sessionManager.getSessionState()

		// Negated condition was hard to read
		if (
			sessionState.state === 'expired-duration' ||
			(sessionState.state === 'expired-inactivity' && this._experimental_discardDataAfterInactivity)
		) {
			// Do not attach session ID to the span
		} else if (
			this._experimental_adjustSessionStartToTimeOrigin &&
			hrTimeToMilliseconds(span.startTime) < sessionState.startTime
		) {
			diag.warn('Dropping span that started before the session', {
				diffMs: sessionState.startTime - hrTimeToMilliseconds(span.startTime),
				sessionStartTime: sessionState.startTime,
				spanName: span.name,
				spanStartTime: hrTimeToMilliseconds(span.startTime),
			})
		} else {
			span.setAttribute('splunk.rumSessionId', sessionState.id)
		}

		const anonymousUserId = this.userManager.getAnonymousIdIfEnabled()
		if (anonymousUserId) {
			span.setAttribute('user.anonymous_id', anonymousUserId)
		}

		span.setAttribute('browser.instance.visibility_state', document.visibilityState)

		// Remove 'unknown_service' service.name from resource
		if (span.resource.attributes['service.name'] === 'unknown_service') {
			delete span.resource.attributes['service.name']
		}
	}

	setGlobalAttributes(attributes?: Attributes): void {
		if (attributes) {
			Object.assign(this._globalAttributes, attributes)
		} else {
			for (const key of Object.keys(this._globalAttributes)) {
				delete this._globalAttributes[key]
			}
		}
	}

	shutdown(): Promise<void> {
		return Promise.resolve()
	}
}
